import { Component, Input, Output, EventEmitter } from '@angular/core';
import type { SummaryTopIssueLine } from '../../../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';

export interface SummaryThemeOption {
  themeId: string;
  themeName: string;
}

@Component({
  selector: 'app-sentiment-summary-generator-widget',
  standalone: false,
  templateUrl: './summary-generator-widget.component.html',
  styleUrls: ['./summary-generator-widget.component.scss'],
})
export class SentimentSummaryGeneratorWidgetComponent {
  /** When false (e.g. summary v2 / precomputed API), the actionable filter is hidden — it is not supported server-side. */
  @Input() showOnlyActionableFilter = true;
  @Input() onlyActionable = true;
  @Input() selectedSentiment: string = '';
  @Input() selectedThemeId: string = '';
  @Input() themes: SummaryThemeOption[] = [];
  @Input() summaryText: string | null = null;
  @Input() summaryStats: { feedbackCount: number; actionableCount: number } | null = null;
  @Input() summaryTopIssues: SummaryTopIssueLine[] = [];
  @Input() summaryNextSteps: string[] = [];
  @Input() generating = false;
  @Input() loadingSummary = false;
  /** From `config.summary.precompute.sentiments` (excludes aggregate `all`). */
  @Input() sentimentOptions: Array<{ value: string; label: string }> = [];

  @Output() onlyActionableChange = new EventEmitter<boolean>();
  @Output() selectedSentimentChange = new EventEmitter<string>();
  @Output() selectedThemeIdChange = new EventEmitter<string>();
  @Output() generate = new EventEmitter<void>();

  /** Ensure option value is string so ngModel matches (fixes preset/selection display). */
  themeIdStr(id: string | number): string {
    return id == null ? '' : String(id);
  }

  /** Index-based trackBy so option list is stable without risking duplicate keys from empty/duplicate IDs. */
  themeTrackBy(index: number): number {
    return index;
  }

  onOnlyActionableChange(checked: boolean): void {
    this.onlyActionableChange.emit(checked);
  }

  onSentimentChange(value: string): void {
    this.selectedSentimentChange.emit(value);
  }

  onThemeChange(value: string | number): void {
    this.selectedThemeIdChange.emit(this.themeIdStr(value));
  }

  /** Split main summary on blank lines; otherwise one block (preserves single newlines inside). */
  summaryParagraphs(text: string | null | undefined): string[] {
    const t = String(text ?? '').trim();
    if (!t) return [];
    if (/\n\s*\n/.test(t)) {
      return t.split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean);
    }
    return [t];
  }

  paraTrackBy(index: number): number {
    return index;
  }

  insightTrackBy(index: number): number {
    return index;
  }

  /** One row per `top_issues[].actionable_insight`, same order as Top issues (v2 only). */
  get summaryActionableInsights(): string[] {
    return this.summaryTopIssues
      .map((i) => String(i.actionableInsight ?? '').trim())
      .filter(Boolean);
  }

  /**
   * v2 LLM: `display_text` and `issue_summary` on one line; legacy: cohort breakdown from facts.
   */
  formatTopIssueLine(issue: SummaryTopIssueLine): string {
    const dt = issue.displayText?.trim();
    const summ = issue.issueSummary?.trim();
    if (dt || summ) {
      return [dt, summ].filter(Boolean).join(' ');
    }
    const { subthemeName, cohortLabel, cohortValue, mentionCount } = issue;
    if (cohortLabel && cohortValue) {
      return `${subthemeName}: ${cohortLabel} - ${cohortValue}(${mentionCount})`;
    }
    if (cohortLabel) {
      return `${subthemeName}: ${cohortLabel}(${mentionCount})`;
    }
    if (cohortValue) {
      return `${subthemeName}: ${cohortValue}(${mentionCount})`;
    }
    return subthemeName;
  }
}
